import { z } from 'zod';
import type { DunningChannel, DunningLevel, PaymentOrderStatus } from '../enums.js';

/**
 * Contrat réseau de l'écran « Factures impayées » et de la génération groupée des relances.
 *
 * La séquence est fixée par la politique du cabinet : un à trois rappels, puis, si le cabinet l'a
 * activée, la préparation d'une requête en ordonnance de paiement. L'étape suivante d'une facture
 * est calculée **par le serveur**, par la même règle que la génération : l'écran ne la devine pas.
 *
 * L'application **prépare** la requête — pièce imprimée, montants, juridiction —, elle ne dépose
 * rien : le dépôt au greffe reste un geste du cabinet.
 */

/** Ce qui empêche de préparer une requête alors que la séquence des rappels est terminée. */
export const paymentOrderBlocks = [
  /** Art. 129 NCPC : le débiteur doit être domicilié ou résider au Grand-Duché. */
  'debtor_not_in_luxembourg',
  /** Art. 129 NCPC : au-delà de 15 000 €, la justice de paix n'est plus compétente. */
  'claim_above_justice_de_paix_limit',
] as const;
export type PaymentOrderBlock = (typeof paymentOrderBlocks)[number];

/** Pourquoi une facture impayée n'a plus d'étape à venir. */
export const dunningSequenceEnds = [
  /** Relances désactivées dans les réglages du cabinet. */
  'dunning_disabled',
  /** Une requête vivante existe déjà : la suite se joue au greffe, plus par lettre. */
  'payment_order_exists',
  /** Tous les rappels sont partis et la requête n'est pas activée. */
  'sequence_complete',
] as const;
export type DunningSequenceEnd = (typeof dunningSequenceEnds)[number];

/**
 * Étape suivante d'une facture impayée. `dueOn` est la date civile à partir de laquelle l'étape
 * est due ; `due` dit si elle l'est aujourd'hui.
 */
export type DunningStepDto =
  | {
      kind: 'reminder';
      level: DunningLevel;
      dueOn: string;
      due: boolean;
      /** Rappel déjà généré et en attente de validation à ce niveau : la génération l'envoie. */
      pendingNoticeId: string | null;
    }
  | {
      kind: 'payment_order';
      dueOn: string;
      /** Faux tant que le délai court, ou si `blockedReason` est posé. */
      due: boolean;
      blockedReason: PaymentOrderBlock | null;
    }
  | { kind: 'none'; reason: DunningSequenceEnd };

/** Dernier rappel envoyé sur la facture. */
export interface UnpaidLastReminderDto {
  level: number;
  sentAt: string;
  channel: DunningChannel | null;
}

/**
 * Une facture impayée : émise ou partiellement payée, reste dû positif. Une facture annulée ou
 * soldée par un avoir n'y figure jamais.
 *
 * L'adresse du patient n'y est pas : `patientHasEmail` suffit à dire comment partira le rappel.
 */
export interface UnpaidInvoiceDto {
  invoiceId: string;
  invoiceNumber: string | null;
  patientId: string;
  patientName: string;
  patientHasEmail: boolean;
  issueDate: string;
  /** Échéance, ou date d'émission quand la facture n'en porte pas. */
  dueDate: string;
  totalCents: number;
  outstandingCents: number;
  /** Intérêts de retard encore dus, fixés par le dernier rappel envoyé. */
  interestDueCents: number;
  /** Jours écoulés depuis l'échéance, à la date du jour. Négatif avant l'échéance. */
  daysOverdue: number;
  lastReminder: UnpaidLastReminderDto | null;
  paymentOrder: { id: string; status: PaymentOrderStatus } | null;
  nextStep: DunningStepDto;
}

export interface UnpaidInvoicesDto {
  items: UnpaidInvoiceDto[];
  /** Vrai quand la liste a été coupée à son plafond : l'écran le dit au lieu de mentir par omission. */
  truncated: boolean;
  /** Interrupteur général des relances du cabinet. */
  dunningEnabled: boolean;
  /** Plafond de la liste, pour que l'écran puisse le nommer. */
  limit: number;
}

/** Fenêtre des relances « envoyées récemment » du résumé, en jours. */
export const DUNNING_SENT_RECENT_DAYS = 30;

/**
 * Résumé du recouvrement, en trois temps : ce qui est à relancer, ce qui attend validation, ce
 * qui est parti. Il alimente l'en-tête de l'onglet Relances et les badges ; les chiffres viennent
 * de la même lecture que la liste des impayés et la génération groupée.
 *
 * `toSend` exclut les factures dont la relance due attend déjà sa validation : elles sont comptées
 * dans `pendingValidation`, une seule fois.
 */
export interface DunningSummaryDto {
  dunningEnabled: boolean;
  /** Factures impayées dont l'échéance est passée. */
  overdue: { count: number; outstandingCents: number };
  /** Factures dont l'étape est due aujourd'hui et pas encore préparée. */
  toSend: { count: number; outstandingCents: number };
  /** Relances générées, en attente de validation. */
  pendingValidation: { count: number; dueCents: number };
  /** Relances envoyées depuis `since` (date civile), soit les `DUNNING_SENT_RECENT_DAYS` derniers jours. */
  sentRecently: { count: number; since: string };
  /** Première date à laquelle une étape à venir deviendra due ; nulle s'il n'y en a pas. */
  nextDueOn: string | null;
}

/** Plafond d'une génération groupée : au-delà, on recommence, l'idempotence le permet. */
export const DUNNING_RUN_MAX_INVOICES = 200;

/**
 * Génération groupée. Sans `invoiceIds`, tout ce qui est dû dans le cabinet ; avec, la sélection,
 * dont seules les factures à étape due sont traitées.
 *
 * `dryRun` rend le même compte rendu **sans rien écrire** : c'est ce que le dialogue de
 * confirmation affiche — combien de courriels, de lettres à imprimer, de requêtes à préparer.
 *
 * **Idempotente** : une facture qui vient d'être relancée n'a plus d'étape due, et la base interdit
 * deux rappels vivants au même niveau comme deux requêtes vivantes sur une facture.
 */
export const dunningRunSchema = z
  .object({
    invoiceIds: z.array(z.uuid()).min(1).max(DUNNING_RUN_MAX_INVOICES).optional(),
    dryRun: z.boolean(),
  })
  .strict();
export type DunningRunInput = z.infer<typeof dunningRunSchema>;

/**
 * Sort d'une facture. `print` n'est pas un échec : le dossier n'a pas d'adresse, la lettre est à
 * imprimer et à poster. `skipped` : rien n'était dû, `errorCode` dit pourquoi.
 */
export const dunningRunActions = ['email', 'print', 'payment_order', 'skipped', 'failed'] as const;
export type DunningRunAction = (typeof dunningRunActions)[number];

export interface DunningRunItemDto {
  invoiceId: string;
  action: DunningRunAction;
  /** Niveau du rappel envoyé, nul pour une requête, un saut ou un échec. */
  level: number | null;
  /** Nuls en simulation. */
  dunningNoticeId: string | null;
  paymentOrderId: string | null;
  /** Code stable : motif du saut (`not_yet_due`, `debtor_not_in_luxembourg`…) ou de l'échec. */
  errorCode: string | null;
}

export interface DunningRunResultDto {
  dryRun: boolean;
  items: DunningRunItemDto[];
  counts: { email: number; print: number; paymentOrder: number; skipped: number; failed: number };
}

/**
 * Un seul PDF pour l'impression : les lettres sans adresse et les requêtes préparées, dans l'ordre
 * donné. Réponse `application/pdf`.
 */
export const dunningPrintSchema = z
  .object({
    dunningNoticeIds: z.array(z.uuid()).max(DUNNING_RUN_MAX_INVOICES),
    paymentOrderIds: z.array(z.uuid()).max(DUNNING_RUN_MAX_INVOICES),
  })
  .strict()
  .refine((value) => value.dunningNoticeIds.length + value.paymentOrderIds.length > 0, {
    message: 'Rien à imprimer',
  });
export type DunningPrintInput = z.infer<typeof dunningPrintSchema>;
